import React, { useEffect, useRef } from "react";
import { EnrichedProgram } from "@/common/course/types";
import useWebview from "@/webview/useWebview";
import Tags from "@/components/Tags";
import useElementEvents from "@/hooks/eventing/useElementEvents";
import { useInView } from "react-intersection-observer";
import {
  DEFAULT_USE_IN_VIEW_OPTIONS,
  ElementType,
} from "@/constants/tagular/main";
import useTranslations from "@/i18n/useTranslations";
import { CarouselComponent } from "./components";

interface CarouselData {
  tagValue?: string;
  tagIcon?: string;
  title?: string;
  extraInfo?: string[];
  backgroundColor?: string;
  highlightText?: string;
}

export type CardType = "product-card" | "program-card" | "new-product-card";

interface ProgramCarouselProps {
  enrichedItems?: EnrichedProgram[] | null | undefined;
  courseName?: string;
  carouselData?: CarouselData;
  cardType?: CardType;
  navigationType?: "default" | "individual-buttons";
  isLoading?: boolean;
  isButtonsCentered?: boolean;
  hasShadow?: boolean;
  carouselDataDesign3?: {
    title?: string;
    description?: string;
    backgroundColor?: string;
    highlightText?: string;
  };
}

function ProgramCarousel({
  enrichedItems,
  courseName,
  carouselData,
  cardType = "product-card", // Default to existing behavior
  navigationType = "default",
  isLoading = false,
  isButtonsCentered = false,
  hasShadow = true,
  carouselDataDesign3,
}: ProgramCarouselProps) {
  const tProgram = useTranslations("Program");
  const { getUrl } = useWebview();

  // Use enriched items or empty array if not available
  const displayItems =
    enrichedItems && enrichedItems.length > 0 ? enrichedItems : [];
  const isOnlyOneItem = displayItems.length === 1;

  // Element events for carousel viewing
  const { elementViewed } = useElementEvents({
    webElement: {
      elementType: ElementType.Component,
      name: "related_programs_carousel_viewed",
      location: "course-detail",
      text: "Related Programs Carousel",
      htmlId: "related-programs-carousel",
    },
  });

  const { ref: carouselRef, inView } = useInView(DEFAULT_USE_IN_VIEW_OPTIONS);

  const hasTriggeredViewEvent = React.useRef(false);

  useEffect(() => {
    if (inView && !hasTriggeredViewEvent.current) {
      elementViewed();
      hasTriggeredViewEvent.current = true;
    }
  }, [inView, elementViewed]);

  if (!displayItems || displayItems.length === 0) {
    if (!isLoading) {
      return null;
    }
  }

  // Extract values from props or use defaults
  const tagValue =
    !carouselData?.tagValue || carouselData.tagValue === "Best Value"
      ? tProgram("bestValue")
      : carouselData.tagValue;
  const carouselTitle =
    carouselData?.title || courseName || tProgram("exploreOurPrograms");
  const carouselExtraInfo = carouselData?.extraInfo || [];
  const carouselBackgroundColor = carouselData?.backgroundColor || "putty-400";
  const carouselBackgroundColorDesign3 =
    carouselDataDesign3?.backgroundColor || "putty-400";
  const carouselIcon = carouselData?.tagIcon || "sale";
  const title =
    carouselDataDesign3?.title === undefined
      ? null
      : `${tProgram("thisCourseIsPartOfPrograms", { count: displayItems.length })} `;

  // Map background color to Tailwind classes
  const getBackgroundClass = (bgColor: string) => {
    const backgroundClasses: Record<string, string> = {
      "fern-100": "bg-fern-100",
      "putty-400": "bg-putty-400",
      "putty-100": "bg-putty-100",
      "putty-200": "bg-putty-200",
      "putty-300": "bg-putty-300",
    };
    return backgroundClasses[bgColor] || "bg-putty-400";
  };

  // Helper function to render title with highlighted text
  const renderTitle = (
    carouselTitle: string,
    highlightText: string | undefined,
  ) => {
    if (!highlightText || !carouselTitle.includes(highlightText)) {
      return carouselTitle;
    }

    const regex = new RegExp(highlightText, "gi");
    const matches = carouselTitle.match(regex);
    const timesHighlightedRepeat = matches ? matches.length : 0;

    if (timesHighlightedRepeat === 0) {
      return <>{carouselTitle}</>;
    }

    const firstIndex = carouselTitle.indexOf(highlightText);
    const before = carouselTitle.substring(0, firstIndex);
    const after = carouselTitle.substring(firstIndex + highlightText.length);

    return (
      <>
        {before}
        <span className="text-brand">{highlightText}</span>
        {after}
      </>
    );
  };

  return (
    <div
      ref={carouselRef}
      className={`pt-6 md:pt-14 pb-8 md:pb-12 ${isOnlyOneItem ? "pr-4 md:pr-14 pl-4 md:pl-14" : "pl-4 md:pl-16"} border border-[#E1DDD1] rounded-xl ${isButtonsCentered ? getBackgroundClass(carouselBackgroundColorDesign3) : getBackgroundClass(carouselBackgroundColor)}`}
    >
      <div className="grid grid-cols-1 md:grid-cols-5 md:gap-6">
        {!isButtonsCentered && (
          <div
            className={`col-span-2 flex flex-col gap-4 place-content-center items-start justify-start md:pt-[16px] ${isOnlyOneItem ? "col-span-3" : ""}`}
          >
            <Tags
              tags={[
                {
                  borderless: true,
                  color: "fern",
                  icon: carouselIcon,
                  text: tagValue,
                },
              ]}
              variant="badges"
            />
            <h2 className="mb-4 !text-[36px] md:text-6xl text-[#00262B] font-bold font-inter mt-1 sm:pt-0">
              {renderTitle(
                carouselTitle,
                carouselData?.highlightText || undefined,
              )}
            </h2>
            <ul className="list-disc pl-5 text-[20px] font-inter leading-9 mb-2">
              {carouselExtraInfo &&
                carouselExtraInfo.length > 0 &&
                carouselExtraInfo.map((info: string, index: number) => (
                  <li key={index} className="leading-snug text-[20px] mb-2">
                    {info}
                  </li>
                ))}
            </ul>
          </div>
        )}

        <div
          className={`${isButtonsCentered ? "col-span-1 md:col-span-5" : isOnlyOneItem ? "col-span-2 relative min-w-[285px]" : "col-span-3"} place-content-center`}
        >
          {isButtonsCentered && (
            <div className="text-center mb-10">
              {title && (
                <h2 className="mb-4 !text-5xl md:text-6xl text-[#00262B] font-black italic font-inter mt-1 sm:pt-0">
                  {renderTitle(
                    title,
                    carouselDataDesign3?.highlightText || undefined,
                  )}
                </h2>
              )}
              {carouselDataDesign3?.description && (
                <p className="text-[18px]">
                  {carouselDataDesign3?.description}
                </p>
              )}
            </div>
          )}
          <CarouselComponent
            items={displayItems}
            getUrl={getUrl}
            cardType={cardType}
            navigationType={navigationType}
            isLoading={isLoading}
            isButtonsCentered={isButtonsCentered}
            hasShadow={hasShadow}
            className={isOnlyOneItem ? "md:flex md:justify-end" : ""}
          />
        </div>
      </div>
    </div>
  );
}

export default ProgramCarousel;
